import { z } from "zod";

// Universal settings store (issue #240, PRD #237 — "Variant B": a typed registry
// in code + generic key→value rows in DB). Each key names ONE operator-editable
// product/business setting; THIS registry is the single source of truth for what
// settings exist, how each validates, its default, and how the admin form renders
// it. A new setting is a registry entry — no migration.
//
// Boundary (ADR-0031 §"settings vs env"): only NON-secret product/business config
// lives here. Secrets & topology (Stripe keys, DB URL, S3/CloudFront, APP_BASE_URL,
// SMTP/SES creds) stay in env; buyer-facing PL labels stay in code/i18n. Labels
// and groups below are OPERATOR-facing PL copy for the admin form, not buyer copy.

// How the admin Settings form renders one field. `number` coerces the input to a
// number before validating against the registry schema; `text` keeps it a string.
export const SETTING_INPUT_TYPES = ["number", "text"] as const;
export type SettingInputType = (typeof SETTING_INPUT_TYPES)[number];
export const SettingInputTypeSchema = z.enum(SETTING_INPUT_TYPES);

// The hard ceiling on how many pillows one order can ever contain: the maximum
// the operator-editable `checkout_max_quantity` setting can be raised to, and the
// sanity guard the shared `CheckoutCreateRequest` enforces at the edge. A dynamic,
// operator-chosen ceiling cannot live in a sync import-time Zod `.max()`, so the
// checkout SERVICE enforces the current value (read through the reader) and this
// constant only bounds the request payload. Distinct from the editable DEFAULT
// (10), which lives in the registry entry below.
export const CHECKOUT_MAX_QUANTITY_LIMIT = 100;

// One registry entry. The generic `S` is retained so `default` is typed to the
// schema's output and `SettingValue<K>` infers precisely from the schema.
export interface SettingDefinition<S extends z.ZodType> {
  schema: S;
  default: z.infer<S>;
  label: string;
  group: string;
  inputType: SettingInputType;
  description?: string;
}

// Preserves the concrete schema type `S` (so downstream `z.infer` stays precise)
// while enforcing that `default` matches the schema and `inputType` is valid.
function defineSetting<S extends z.ZodType>(def: SettingDefinition<S>): SettingDefinition<S> {
  return def;
}

// The three settings this slice migrates out of hard-coded constants (#240).
export const SETTINGS_REGISTRY = {
  checkout_max_quantity: defineSetting({
    schema: z.number().int().min(1).max(CHECKOUT_MAX_QUANTITY_LIMIT),
    default: 10,
    label: "Maksymalna liczba sztuk w zamówieniu",
    group: "Zamówienia",
    inputType: "number",
    description: "Górny limit stepper-a ilości na checkoucie i walidacji zamówienia.",
  }),
  checkout_product_name: defineSetting({
    schema: z.string().min(1).max(200),
    default: "Pufimi custom pillow",
    label: "Nazwa produktu (Stripe)",
    group: "Zamówienia",
    inputType: "text",
    description: "Nazwa pozycji pokazywana na stronie płatności Stripe.",
  }),
  owner_notification_email: defineSetting({
    schema: z.email(),
    default: "orders@pufimi.pl",
    label: "E-mail powiadomień o zamówieniu",
    group: "Powiadomienia",
    inputType: "text",
    description: "Adres, na który trafia wewnętrzne powiadomienie o opłaconym zamówieniu.",
  }),
  // The silhouette ships in one of TWO physical boxes (Leon, 2026-07-12). The LARGE
  // box (`_max_long` × `_max_short`) is the biggest the pillow can ever be — the top
  // of the size slider. The SMALL box (`_small_long` × `_small_short`) is the S/L
  // pricing threshold: a pillow that fits inside it (BOTH sides ≤) ships small and
  // bills as the cheaper tier; anything that spills past either axis bills as large.
  // All four are `[longer, shorter]` — the pillow's LONGER side is compared with the
  // longer axis and its SHORTER side with the shorter, whichever way the photo faces.
  silhouette_max_long_cm: defineSetting({
    schema: z.number().int().min(20).max(300),
    default: 70,
    label: "Duże pudełko — dłuższy bok (cm)",
    group: "Produkt",
    inputType: "number",
    description:
      "Dłuższy bok największego pudełka — górna granica suwaka rozmiaru w podglądzie. Poduszka jest skalowana tak, by zmieścić się w tym pudełku przy proporcjach zwierzaka.",
  }),
  silhouette_max_short_cm: defineSetting({
    schema: z.number().int().min(20).max(300),
    default: 40,
    label: "Duże pudełko — krótszy bok (cm)",
    group: "Produkt",
    inputType: "number",
    description:
      "Krótszy bok największego pudełka. Ogranicza rozmiar poduszki od kwadratowej / poziomej strony (przy 2:1 to dłuższy bok wiąże pierwszy). Był stałą w kodzie.",
  }),
  silhouette_small_long_cm: defineSetting({
    schema: z.number().int().min(10).max(300),
    default: 50,
    label: "Próg (małe pudełko) — dłuższy bok (cm)",
    group: "Produkt",
    inputType: "number",
    description:
      "Dłuższy bok mniejszego pudełka. Poduszka jest „mała” (tańsza), gdy mieści się w małym pudełku OBOMA bokami; jeśli którykolwiek bok przekroczy próg — „duża”.",
  }),
  silhouette_small_short_cm: defineSetting({
    schema: z.number().int().min(10).max(300),
    default: 35,
    label: "Próg (małe pudełko) — krótszy bok (cm)",
    group: "Produkt",
    inputType: "number",
    description:
      "Krótszy bok mniejszego pudełka. Poduszka jest „mała” tylko gdy jej krótszy bok też mieści się poniżej tego progu.",
  }),
  // Flat per-method InPost shipping cost in GROSZE (#255). The checkout service
  // reads these via `shippingForCart` / `cartShippingAmountFor` and charges the
  // chosen method as a separate Stripe line item; the client never sends a price.
  // `0` = free shipping for that method. Defaults are sensible PL prices (12,99 / 18,99 zł).
  shipping_cost_paczkomat_cents: defineSetting({
    schema: z.number().int().min(0),
    default: 1299,
    label: "Koszt dostawy — Paczkomat (grosze)",
    group: "Dostawa",
    inputType: "number",
    description: "Zryczałtowany koszt wysyłki do Paczkomatu InPost, w groszach. 0 = darmowa.",
  }),
  shipping_cost_courier_cents: defineSetting({
    schema: z.number().int().min(0),
    default: 1899,
    label: "Koszt dostawy — Kurier InPost (grosze)",
    group: "Dostawa",
    inputType: "number",
    description: "Zryczałtowany koszt wysyłki kurierem InPost, w groszach. 0 = darmowa.",
  }),
  // Cart oversize rule (ADR-0054 §4, #406). The whole order ships as ONE parcel; each
  // Variant carries a shipping-size class (parcel size units), and `shippingForCart` sums
  // `units × quantity` across the cart. When the total exceeds this Paczkomat capacity —
  // expressed in the SAME size units — the order can no longer fit a locker: Paczkomat is
  // withdrawn and the buyer is forced to Kurier InPost. Default 6 units: a large pillow is
  // 2 units and a small is 1, so roughly three large pillows still fit a locker before an
  // order tips to courier — a friendly default the operator tunes for their real parcels.
  shipping_paczkomat_max_size_units: defineSetting({
    schema: z.number().int().min(1),
    default: 6,
    label: "Pojemność Paczkomatu (jednostki rozmiaru)",
    group: "Dostawa",
    inputType: "number",
    description:
      "Maksymalna suma klas rozmiaru wysyłki (jednostek) całego zamówienia, która jeszcze mieści się w Paczkomacie. Powyżej — zamówienie trafia na Kuriera InPost.",
  }),
  // Optional surcharge (grosze) folded into the flat Kurier rate when the order is forced
  // to courier by the oversize rule above (#406). `0` (default) = no surcharge: an oversize
  // order simply pays the normal Kurier flat rate. Raise it to bill the extra cost of a
  // bulky parcel. Never applied below the threshold.
  shipping_oversize_surcharge_cents: defineSetting({
    schema: z.number().int().min(0),
    default: 0,
    label: "Dopłata za duże zamówienie — Kurier (grosze)",
    group: "Dostawa",
    inputType: "number",
    description:
      "Dopłata doliczana do zryczałtowanego kosztu Kuriera InPost, gdy zamówienie przekroczy pojemność Paczkomatu. 0 = brak dopłaty.",
  }),
};

export type SettingKey = keyof typeof SETTINGS_REGISTRY;
export type SettingValue<K extends SettingKey> = z.infer<(typeof SETTINGS_REGISTRY)[K]["schema"]>;
export type SettingsValues = { [K in SettingKey]: SettingValue<K> };

// Stable ordered list of registry keys — the admin form + the reader iterate it.
export const SETTING_KEYS = Object.keys(SETTINGS_REGISTRY) as SettingKey[];

// Resolve ONE setting from its raw stored JSON (or `undefined` when the row is
// absent): valid stored value wins; an absent OR invalid value falls back to the
// registry default. Never throws — a malformed row must not take down a read
// (issue #240). This is the pure read-semantics core the DB reader composes over.
export function resolveSetting<K extends SettingKey>(key: K, raw: unknown): SettingValue<K> {
  const def = SETTINGS_REGISTRY[key];
  if (raw === undefined) return def.default as SettingValue<K>;
  const parsed = (def.schema as z.ZodType).safeParse(raw);
  return (parsed.success ? parsed.data : def.default) as SettingValue<K>;
}

// Resolve EVERY registry key from a map of raw stored values (missing keys ⇒
// default), returning the full typed set for the admin form / `getAll`.
export function resolveSettings(rawByKey: ReadonlyMap<string, unknown>): SettingsValues {
  return Object.fromEntries(
    SETTING_KEYS.map((key) => [key, resolveSetting(key, rawByKey.get(key))]),
  ) as SettingsValues;
}

// Serializable metadata for the admin form — the registry MINUS the Zod schema
// (which can't cross the wire; the SPA imports the registry directly to validate
// client-side). The API pairs each entry with the current value from the reader.
export function settingsRegistryMetadata(): Array<Omit<AdminSettingField, "value">> {
  return SETTING_KEYS.map((key) => {
    const def = SETTINGS_REGISTRY[key];
    return {
      key,
      label: def.label,
      group: def.group,
      inputType: def.inputType,
      description: def.description ?? null,
    };
  });
}

// ── Admin API transport contracts ──────────────────────────────────────────
// Shared so the API and the `packages/admin` SPA validate the same shapes.

// One field of `GET /admin/settings`: registry metadata + the current EFFECTIVE
// value (stored, or the registry default when unset). `value` is `unknown` on the
// wire — its per-key shape is recovered on the client via the imported registry.
export const AdminSettingField = z.object({
  key: z.string(),
  label: z.string(),
  group: z.string(),
  inputType: SettingInputTypeSchema,
  description: z.string().nullable(),
  value: z.unknown(),
});
export type AdminSettingField = z.infer<typeof AdminSettingField>;

// Response of `GET /admin/settings` — every registry setting with its metadata +
// current value, in registry order (the SPA groups by `group`).
export const AdminSettingsResponse = z.object({ settings: z.array(AdminSettingField) });
export type AdminSettingsResponse = z.infer<typeof AdminSettingsResponse>;

// Body of `PUT /admin/settings/:key` — the raw candidate value. The API re-validates
// it against the registry's Zod schema for that key (the real gate); `unknown` here
// just lets any JSON reach that check.
export const AdminSettingUpdateRequest = z.object({ value: z.unknown() });
export type AdminSettingUpdateRequest = z.infer<typeof AdminSettingUpdateRequest>;

// Response of `PUT /admin/settings/:key` — the updated field (same shape as one
// list row) so the SPA can drop it straight back into its cache.
export const AdminSettingUpdateResponse = AdminSettingField;
export type AdminSettingUpdateResponse = z.infer<typeof AdminSettingUpdateResponse>;
